import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetCompanyCostsQuery } from "@/pages/Private/redux/media/media.api";

import { usePageTitle } from "@/utils/usePageTitle";

import { CompanyCostsBreakdown } from "./components/CompanyCostsBreakdown";
import { CostWindowPicker } from "./components/CostWindowPicker";
import { UnverifiedPricingNotice } from "../MediaPresets/components/UnverifiedPricingNotice";

/** `YYYY-MM-DD`, which is what the endpoint takes and what a date input produces. */
const isoDate = (date: Date) => date.toISOString().slice(0, 10);

/**
 * What this company has spent on AI media.
 *
 * A route of its own rather than a fourth section on the presets page: that page is
 * platform administration and this is a company reading its own spend, on a different
 * permission and for a different person.
 *
 * The window is always stated. An "all time" total silently changes meaning as the product
 * ages, so the server applies a twelve-month default and reports the window it used, and
 * this page shows that rather than the one it asked for.
 */
export const MediaCostsPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`mediaCosts.${key}`, options ?? {});

	usePageTitle(ts("title"));

	const [window, setWindow] = useState<{ from?: string; to?: string }>({});

	const { data: costs, isFetching } = useGetCompanyCostsQuery(window, {
		refetchOnFocus: true,
	});

	const applied = useMemo(
		() =>
			costs
				? { from: isoDate(new Date(costs.from)), to: isoDate(new Date(costs.to)) }
				: { from: "", to: "" },
		[costs]
	);

	return (
		<div className="space-y-5 p-6 pb-10">
			<p className="max-w-page text-sm text-subtle">{ts("description")}</p>

			{/* A total built from placeholder prices is a placeholder total, and the page has
			    to say so where the figure is, not somewhere else. */}
			<UnverifiedPricingNotice unverifiedCount={costs && !costs.pricesVerified ? 1 : 0} />

			<CostWindowPicker
				applied={applied}
				isFetching={isFetching}
				window={window}
				onChange={setWindow}
				onReset={() => setWindow({})}
			/>

			<CompanyCostsBreakdown costs={costs} isFetching={isFetching} />
		</div>
	);
};
